import { UserAvatars } from '@mitel/cloudlink-sdk/admin';
import Dexie from 'dexie';

class ChatDatabase extends Dexie {
  // Declare implicit table properties.
  // (just to inform Typescript. Instanciated by Dexie in stores() method)
  conversations: Dexie.Table<IChatDbConversation, [string, string]>;
  messages: Dexie.Table<IChatDbMessage, [string, string]>; // type of the primkey
  users: Dexie.Table<IChatUser, [string, string]>;
  participants: Dexie.Table<IChatParticipant, [string, string, string]>;

  //...other tables goes here...

  constructor() {
    super('CLComponentChatDatabase');

    this.version(5).stores({
      conversations: '[accountId+conversationId], indexDbLoadedOn',
      messages:
        '[accountId+messageId], [accountId+conversationId], conversationId, contentType, createdOn, createdBy, modifiedOn, modifiedBy, state',
      users: '[accountId+principalId], indexDbLoadedOn, displayName, email, isLoggedIn',
      participants: '[accountId+conversationId+principalId]'
      //...other tables goes here...
    });

    // The following line is needed if your typescript
    // is compiled using babel instead of tsc:
    this.conversations = this.table('conversations');
    this.messages = this.table('messages');
    this.users = this.table('users');

    this.conversations.mapToClass(ChatDbConversation);
  }
}

export interface IChatDbConversation {
  accountId: string;
  conversationId: string;
  indexDbLoadedOn: string;
  lastReadMessageDate?: string; // Not indexed
}

export interface IChatDbMessage {
  accountId: string;
  messageId: string;
  conversationId: string;
  body: string; // Not indexed
  createdOn: string;
  contentType: string;
  createdBy: string;
  modifiedOn: string;
  modifiedBy: string;
  fallbackText?: string; // Not indexed
  tag?: string; // Not indexed
  attachmentName?: string; // Not indexed
  attachmentSize?: string; // Not indexed
  state?: string; // Not indexed
  isPendingSend?: string;
  smsMessage?: boolean; // Not indexed
}

export interface IChatUser {
  accountId: string;
  principalId: string;
  indexDbLoadedOn: string;
  displayName: string;
  avatars: UserAvatars; // Not indexed
  email?: string;
  isLoggedIn?: string;
}

export interface IChatParticipant {
  accountId: string;
  conversationId: string;
  principalId: string;
  lastReadMessageDate: string; // Not indexed
  participantType: string; // Not indexed
}

//reactions
//translations

export class ChatDbConversation implements IChatDbConversation {
  accountId: string;
  conversationId: string;
  indexDbLoadedOn: string;
  messages: IChatDbMessage[];
  lastReadMessageDate: string;

  constructor(conversationId: string, accountId: string, indexDbLoadedOn?: string, lastReadMessageDate?: string) {
    this.conversationId = conversationId;
    this.accountId = accountId;
    this.indexDbLoadedOn = indexDbLoadedOn;
    this.lastReadMessageDate = lastReadMessageDate;

    // Define navigation properties.
    // Making them non-enumerable will prevent them from being handled by indexedDB
    // when doing put() or add().
    Object.defineProperties(this, {
      messages: { value: [], enumerable: false, writable: true }
    });
  }

  async loadNavigationProperties() {
    [this.messages] = await Promise.all([
      chatDb.messages
        .where({
          accountId: this.accountId,
          conversationId: this.conversationId
        })
        .toArray()
    ]);
  }

  async save(excludeConversation = false) {
    // Add or update our selves. If add, record this.id.
    if (!excludeConversation) {
      var primKey = await chatDb.conversations.put(this);
      this.accountId = primKey[0];
      this.conversationId = primKey[1];
    }

    // Save all navigation properties
    // Some may be new and some may be updates of existing objects.
    // put() will handle both cases.
    // (record the result keys from the put() operations
    // so that we can find local deletes)
    // Do not save pending states, let the conversation service handle that
    let [keys] = await Promise.all([
      Promise.all(this.messages.filter(m => m.state !== 'pending').map(message => chatDb.messages.put(message)))
    ]);

    let messageIds = keys.map(a => a[1]);

    this.messages
      .filter(m => m.state === 'pending')
      .forEach(p => {
        messageIds.push(p.messageId);
      });
    // Any deleted from out navigation properties?
    // Delete any item in DB that reference us, but is not present
    // in our navigation properties:
    await Promise.all([
      chatDb.messages
        .where({
          accountId: this.accountId,
          conversationId: this.conversationId
        }) // references us
        .and(message => messageIds.indexOf(message.messageId) === -1 && message.state !== 'pending') // Not anymore in our array
        .delete()
    ]);
  }
}

export var chatDb = new ChatDatabase();
